// ============================================================================
// LOCATION CSV TEMPLATE GENERATOR
// ============================================================================
// Utilities for generating and downloading location import CSV templates
// ============================================================================

export interface LocationTemplateRow {
  name: string;
  code?: string;
  address?: string;
}

/**
 * Generate a sample location import CSV template
 */
export const generateLocationImportTemplate = (): string => {
  const headers = ['name', 'code', 'address'];
  
  const sampleData: LocationTemplateRow[] = [
    {
      name: 'Aston Martin Beverly Hills',
      code: 'AM-BH-001',
      address: '9440 Wilshire Blvd, Beverly Hills, CA 90212, USA'
    },
    {
      name: 'Aston Martin Manhattan',
      code: 'AM-NY-001',
      address: '420 Park Avenue, New York, NY 10022, USA'
    },
    {
      name: 'Aston Martin London',
      code: 'AM-LON-001',
      address: '18 Dover Street, Mayfair, London W1S 4LT, UK'
    },
    {
      name: 'Aston Martin Munich',
      code: 'AM-MUN-001',
      address: 'Maximilianstrasse 35, 80539 München, Germany'
    },
    {
      name: 'Aston Martin Tokyo',
      code: 'AM-TOK-001',
      address: '6-10-1 Roppongi, Minato City, Tokyo 106-0032, Japan'
    }
  ];
  
  // Create CSV content
  const csvRows = [
    headers.join(','), // Header row
    ...sampleData.map(row => [
      `"${row.name}"`,
      `"${row.code || ''}"`,
      `"${row.address || ''}"`
    ].join(','))
  ];
  
  return csvRows.join('\n');
};

/**
 * Download a CSV file with the given content
 */
export const downloadCSVFile = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse location import CSV data for preview
 */
export const parseLocationImportData = (csvContent: string): {
  isValid: boolean;
  headers: string[];
  rows: string[][];
  errors: string[];
} => {
  try {
    const lines = csvContent.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      return {
        isValid: false,
        headers: [],
        rows: [],
        errors: ['CSV file is empty']
      };
    }
    
    // Parse headers
    const headers = parseCSVRow(lines[0]);
    
    // Validate required headers
    const requiredHeaders = ['name'];
    const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
    const missingHeaders = requiredHeaders.filter(required => 
      !normalizedHeaders.some(h => h.includes(required))
    );
    
    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        headers,
        rows: [],
        errors: [`Missing required columns: ${missingHeaders.join(', ')}`]
      };
    }
    
    // Parse data rows
    const rows = lines.slice(1).map(line => parseCSVRow(line));
    
    // Basic validation
    const errors: string[] = [];
    rows.forEach((row, index) => {
      if (row.length > 0 && !row[0]?.trim()) {
        errors.push(`Row ${index + 2}: Location name is required`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      headers,
      rows,
      errors
    };
    
  } catch (error) {
    return {
      isValid: false,
      headers: [],
      rows: [],
      errors: [`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`]
    };
  }
};

/**
 * Parse a single CSV row, handling quoted values
 */
const parseCSVRow = (row: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < row.length) {
    const char = row[i];
    const nextChar = row[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i += 2;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      result.push(current.trim());
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }

  // Add the last field
  result.push(current.trim());
  return result.map(field => {
    // Remove surrounding quotes if present
    if (field.startsWith('"') && field.endsWith('"')) {
      return field.slice(1, -1);
    }
    return field;
  });
};
















